import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'

// The meter's address as the tickets portal and the ERP know it: the tickets location (id = postcode +
// house number, e.g. 1234AB12-A) gives the legacy address, the key of ERP actions, tasks and groups.
// The ERP groups of that address give its projects: since multi-project addresses (EH-1824) an
// address can be in more than one.
export function useAddressLocation(meterDetail) {
  const locationId = computed(() => {
    if (!meterDetail.value) return null
    const { zip_code, zip_code_ext, house_number } = meterDetail.value
    if (!zip_code || !house_number) return null
    return `${zip_code}${zip_code_ext || ''}${String(house_number).replace(/\s+/g, '-')}`.toUpperCase()
  })

  const { data: location } = useQuery({
    queryKey: computed(() => ['pollingLocation', locationId.value]),
    queryFn: () => fetchData(`/tickets/api/v1/locations/${locationId.value}`).then((r) => r?.data ?? null),
    enabled: computed(() => !!locationId.value),
    refetchOnWindowFocus: false,
  })

  const legacyAddress = computed(() => location.value?.legacy_address ?? null)
  const woco = computed(() => location.value?.woco_shortname ?? '')

  const { data: erpAddress } = useQuery({
    queryKey: computed(() => ['erpAddress', legacyAddress.value]),
    queryFn: () => fetchData(`/api/v1/address/${legacyAddress.value}`).then((r) => r?.data ?? null),
    enabled: computed(() => !!legacyAddress.value),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  // distinct, in the order of the groups
  const projects = computed(() => [
    ...new Set((erpAddress.value?.groups ?? []).map((g) => g.project).filter(Boolean)),
  ])

  return { locationId, location, legacyAddress, woco, projects }
}
